import {
  getSubstitutionMesh,
  getDeletionMesh,
  getAdditionMesh,
  getSelectionMesh,
  drawVariant,
  drawSelection,
  updateVis,
  colorMap,
} from "./VariantBlueprint";
import { getSelectionColorMap } from "../../constants/colors";
import partition from "../../../../common/helpers/partition";
import packSmallRegions from "../../helpers/packRegions/packSmallRegions";
import { variantDbs } from "../../constants/dbs";
import flatten from "../../helpers/flatten";
import Filter from "../../../../models/Filter";
import Variant from "../../../../models/Variant";
import VariantDBDetails from "../../../../models/VariantDBDetails";

const dbs = Object.values(variantDbs);
function sortRefLen(a, b) {
  return b.variant.refLength - a.variant.refLength;
}

function getDefaultState() {
  return {
    config: {},
    allVariants: [],
    //
    substitutions: [],
    additions: [],
    deletions: [],
    selections: [],
    //
    floorHeight: 16,
    variantDict: {},
    maxFloor: 0,
    floors: [],
    visRange: [0, 1],
    filters: dbs.map(
      ({ name: label, key, hex: color, category }) =>
        new Filter({
          label,
          key,
          color,
          value: 0,
          category,
        })
    ),
  };
}

function getVariantsTrackController(track) {
  let state = getDefaultState();
  const selectionsMesh = getSelectionMesh();
  const substitutionMesh = getSubstitutionMesh();
  const deletionMesh = getDeletionMesh();
  const additionMesh = getAdditionMesh();

  function onReset() {
    state = getDefaultState();
  }

  function onInit() {
    track.setFilters(state.filters);
  }

  function onRangeChange({ chrSegments, start, end }) {
    state.visRange = [start, end];
    track.loadPosition(chrSegments);
  }

  function onFiltersChange(updated) {
    state.filters = updated;
    track.setFilters([...state.filters]);
    setTimeout(() => {
      applyFilters();
    }, 100);
  }

  function getActiveFilters() {
    return state.filters.filter((x) => x.value);
  }

  function applyFiltersToVariants(items, activeFilters) {
    const dict = activeFilters
      .map((x) => x.key)
      .reduce((a, i) => a.set(i, true), new Map());

    for (let i = 0; i < items.length; i++) {
      items[i].hidden = !dict.get(items[i].db.key);
    }
  }

  function applyFilters() {
    const activeFilters = getActiveFilters();
    const hasFilters = Boolean(activeFilters.length);
    if (activeFilters.length) {
      applyFiltersToVariants(state.allVariants, activeFilters);
    }
    packSmallRegions(state.allVariants, [], hasFilters);
    updateVis(state.substitutions, substitutionMesh, 0.05);
    updateVis(state.deletions, deletionMesh, 0.05);
    updateVis(state.additions, additionMesh, 0);
    updateSelection();
    update();
  }

  function processVariants(response, chr) {
    const detailsArr = Object.entries(response)
      .filter(([db, versions]) => variantDbs[db] && Array.isArray(versions))
      .map(([dbName, dbVersions]) => {
        return dbVersions
          .filter((v) => Array.isArray(v?.variants))
          .map(({ variants }) => {
            const db = variantDbs[dbName];
            return variants.map((v) => {
              const existing = track.variantRegistry.get(v.variant_id);
              const variant = existing || new Variant(v, chr);
              if (!existing) track.variantRegistry.set(variant.id, variant);
              return new VariantDBDetails(v, variant, db);
            });
          });
      });

    return flatten(flatten(detailsArr));
  }

  function deduplicate(dbDetails) {
    return dbDetails.filter((v) => {
      if (state.variantDict[v.id]) return false;
      state.variantDict[v.id] = v;
      return true;
    });
  }

  function partitionVariants(variants = []) {
    const isAddition = (x) => x.variant.refLength === 0;
    const isDeletion = (x) => x.variant.altLength === 0;
    const [subs, adds, dels] = partition(variants, isAddition, isDeletion);
    return { subs, adds, dels };
  }

  function onDataReceived({ res, chr }) {
    const vars = processVariants(res, chr);
    const uniqueVars = deduplicate(vars);
    if (uniqueVars.length === 0) return;
    const sortedUnique = uniqueVars.sort(sortRefLen);
    const items = sortedUnique;
    const { subs, adds, dels } = partitionVariants(sortedUnique);
    const activeFilters = getActiveFilters();
    const hasFilters = Boolean(activeFilters.length);
    const { floors } = state;

    state.allVariants = state.allVariants.concat(sortedUnique);
    state.substitutions = state.substitutions.concat(subs);
    state.deletions = state.deletions.concat(dels);
    state.additions = state.additions.concat(adds);

    substitutionMesh.setItems(state.substitutions);
    deletionMesh.setItems(state.deletions);
    additionMesh.setItems(state.additions);

    applyFiltersToVariants(sortedUnique, activeFilters);
    packSmallRegions(items, floors, hasFilters);
    drawVariant(subs, substitutionMesh, 0.05);
    drawVariant(dels, deletionMesh, 0.05);
    drawVariant(adds, additionMesh, 0);

    state.maxFloor = state.floors.length;

    updateSelection();
    recalcHeight();
    calcFilters();
    update();
  }

  function calcFilters() {
    const dictCountDict = state.filters
      .map(({ key }) => key)
      .reduce((a, i) => ({ ...a, [i]: 0 }), {});

    for (let i = 0; i < state.allVariants.length; i++) {
      const x = state.allVariants[i];
      dictCountDict[x.db.key] += 1;
    }

    state.filters.forEach((x) => x.setTotalCount(dictCountDict[x.key]));
    track.setFilters([...state.filters]);
  }

  function updateSelection() {
    const { selectedVariantId } = track.getSharedState();
    selectionsMesh.reset();

    if (!selectedVariantId) return;

    state.selections = dbs
      .map((x) => x.index)
      .map((x) => `${x}-${selectedVariantId}`)
      .map((x) => state.variantDict[x])
      .filter(Boolean);

    selectionsMesh.setItems(state.selections);
    drawSelection(state.selections, selectionsMesh, 0.05);
  }

  function update() {
    const floors = state.maxFloor || 1;
    substitutionMesh.uniforms.setMaxY(floors);
    deletionMesh.uniforms.setMaxY(floors);
    additionMesh.uniforms.setMaxY(floors);
    selectionsMesh.uniforms.setMaxY(floors);
    track.setHeight(floors * state.floorHeight);
    track.requestRender();
  }

  function onPreferencesChanged() {
    state.config = track.getPreferences();
    updateColorMap();
  }

  function updateColorMap(highlight) {
    const { config } = state;
    const { vectors } = colorMap.calc(config, highlight);
    const map = colorMap.calc(config);
    const vec = getSelectionColorMap(config.isDark);

    substitutionMesh.uniforms.setColorMap(vectors);
    deletionMesh.uniforms.setColorMap(vectors);
    additionMesh.uniforms.setColorMap(vectors);
    selectionsMesh.uniforms.setColorMap([vec, vec]);

    state.filters.forEach((x) => x.setColor(map.getKey(x.key)?.color?.hex));

    track.setFilters([...state.filters]);
    update();
  }

  function onFilterHighlight(key) {
    updateColorMap(key);
  }

  function onSharedStateChange() {
    updateSelection();
  }

  function recalcHeight() {
    const [start, end] = state.visRange;
    for (let i = state.floors.length - 1; i > 0; i--) {
      const positions = Object.keys(state.floors[i]).map((x) => Number(x));
      for (let x = 0; x < positions.length; x++) {
        const p = positions[x];
        if (p > start && p < end) {
          state.maxFloor = i;
          return;
        }
      }
    }
    state.maxFloor = 0;
  }

  function onRangeStabilized() {
    recalcHeight();
    update();
  }

  return {
    onReset,
    onInit,
    onFiltersChange,
    onRangeChange,
    onDataReceived,
    onFilterHighlight,
    onPreferencesChanged,
    onSharedStateChange,
    onRangeStabilized,
    meshes: [substitutionMesh, deletionMesh, additionMesh, selectionsMesh],
  };
}

export default getVariantsTrackController;
